import { useEffect, useRef } from 'react';
import useEditorStore, { Draft } from './editorStore';

/**
 * Real autosave for the canvas editor: 1.5s after the last change, every
 * dirty draft (post or layout) is flushed through `save`. One flight at a
 * time — edits made while a save is in the air re-dirty the draft and are
 * picked up by the next tick. On failure the dirty keys are requeued (no
 * edit is dropped) and the top-bar chip flips to the error state until a
 * later flush succeeds.
 */

const DEBOUNCE_MS = 1500;

// `key` is the entityKey ("post:<id>" | "layout:<id>"); the callback
// dispatches on the draft's `kind`.
export type SaveDraft = (key: string, draft: Draft) => Promise<unknown>;

export default function useAutosave(save: SaveDraft) {
  const saveRef = useRef(save);
  saveRef.current = save;
  const inFlight = useRef(false);

  // editSeq bumps on every edit, so the timer re-arms per keystroke and the
  // flush genuinely runs "1.5s after the LAST change" (dirtyKeys.length alone
  // would fire 1.5s after the FIRST change of a burst).
  const editSeq = useEditorStore((s) => s.editSeq);
  const dirtyCount = useEditorStore((s) => s.dirtyKeys.length);

  useEffect(() => {
    if (dirtyCount === 0) return undefined;

    const timer = setTimeout(async () => {
      if (inFlight.current) return; // next edit re-arms the timer
      const { takeDirty, requeueDirty, setSaveState } = useEditorStore.getState();
      const keys = takeDirty();
      if (keys.length === 0) return;

      inFlight.current = true;
      setSaveState('saving');
      const failed: string[] = [];
      let lastError: string | null = null;
      for (const key of keys) {
        // Read the draft at send time, not claim time — later keystrokes in
        // the same burst should ride along.
        const draft = useEditorStore.getState().drafts[key];
        if (!draft) continue;
        try {
          await saveRef.current(key, draft);
        } catch (err) {
          failed.push(key);
          lastError = err instanceof Error ? err.message : 'Save failed';
        }
      }
      inFlight.current = false;

      if (failed.length > 0) {
        requeueDirty(failed);
        setSaveState('error', lastError);
      } else if (useEditorStore.getState().dirtyKeys.length > 0) {
        // New edits arrived mid-save; the re-armed timer will flush them.
        setSaveState('dirty');
      } else {
        setSaveState('saved');
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [editSeq, dirtyCount]);
}
